import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { LoadingPanel, Section } from "../shared";
import type { AffiliateAuditLog } from "../types";

export function AuditLogTab({ affiliateId }: { affiliateId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["affiliate-audit-log", affiliateId],
    queryFn: () => api.affiliates.getAuditLog(affiliateId),
    enabled: !!affiliateId,
  });

  const entries: AffiliateAuditLog[] = data?.entries ?? [];

  return (
    <Section title={`Audit Log (${entries.length})`}>
      {isLoading ? (
        <LoadingPanel />
      ) : error ? (
        <Text className="text-sm text-red-600">
          Failed to load audit log: {(error as Error)?.message}
        </Text>
      ) : (
        <Table dense striped>
          <TableHead>
            <TableRow>
              <TableHeader>When</TableHeader>
              <TableHeader>Action</TableHeader>
              <TableHeader>Admin</TableHeader>
              <TableHeader>Reason</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-6 text-center">
                  <Text className="text-sm text-zinc-500">No audit entries</Text>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-sm text-zinc-500">{formatDate(e.timestamp)}</TableCell>
                  <TableCell>
                    <Badge color="blue">{e.action}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {e.adminEmail || <span className="font-mono text-xs">{e.adminUserId}</span>}
                  </TableCell>
                  <TableCell className="max-w-[360px] truncate text-sm text-zinc-500">
                    {e.reason || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}
